/**
 * Pestañas (páginas) del reporte custom.
 */
import React, { useState } from 'react';

const ReportPageNav = ({
    pages = [],
    activePageId,
    onSelect,
    editable = false,
    onAdd,
    onDelete,
    onRename,
    onDuplicate,
    onOpenOutline,
    compact = false
}) => {
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState('');
    const index = Math.max(0, pages.findIndex(page => page.id === activePageId));
    const current = pages[index] || pages[0];

    if (!pages.length) return null;
    if (!editable && pages.length < 2) return null;

    const startRename = (page) => {
        if (!editable || !onRename) return;
        setEditingId(page.id);
        setDraft(page.name || '');
    };

    const commitRename = () => {
        const name = draft.trim();
        if (editingId && name && onRename) onRename(editingId, name);
        setEditingId(null);
    };

    if (compact) {
        return (
            <div style={styles.compact} data-export-ignore="true">
                <button
                    type="button"
                    style={styles.arrow}
                    disabled={index <= 0}
                    onClick={() => onSelect(pages[index - 1].id)}
                    title="Hoja anterior"
                >
                    ‹
                </button>
                <div
                    style={styles.compactLabel}
                    title="Abrir índice de hojas"
                    onClick={onOpenOutline}
                    role="button"
                >
                    <span style={styles.compactTitle}>{current?.name || 'Hoja'}</span>
                    <span style={styles.pager}>Página {index + 1} de {pages.length}</span>
                </div>
                <button
                    type="button"
                    style={styles.arrow}
                    disabled={index >= pages.length - 1}
                    onClick={() => onSelect(pages[index + 1].id)}
                    title="Hoja siguiente"
                >
                    ›
                </button>
                {editable && onAdd && (
                    <button type="button" style={styles.compactAdd} onClick={onAdd} title="Añadir hoja">+</button>
                )}
            </div>
        );
    }

    return (
        <div style={styles.bar} data-export-ignore="true">
            <button
                type="button"
                style={styles.arrow}
                disabled={index <= 0}
                onClick={() => onSelect(pages[index - 1].id)}
                title="Hoja anterior"
            >
                ‹
            </button>
            <div style={styles.tabs}>
                {pages.map((page) => {
                    const active = page.id === (current?.id);
                    return (
                        <div key={page.id} style={active ? styles.tabOn : styles.tab}>
                            {editingId === page.id ? (
                                <input
                                    autoFocus
                                    value={draft}
                                    onChange={e => setDraft(e.target.value)}
                                    onBlur={commitRename}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            commitRename();
                                        }
                                        if (e.key === 'Escape') setEditingId(null);
                                    }}
                                    style={styles.rename}
                                />
                            ) : (
                                <button
                                    type="button"
                                    style={styles.tabBtn}
                                    onClick={() => onSelect(page.id)}
                                    onDoubleClick={() => startRename(page)}
                                    title={editable ? 'Doble clic para renombrar' : page.name}
                                >
                                    {page.name}
                                </button>
                            )}
                            {editable && active && (
                                <div style={styles.tabActions}>
                                    <button type="button" style={styles.iconBtn} title="Renombrar" onClick={() => startRename(page)}>✎</button>
                                    <button
                                        type="button"
                                        style={pages.length <= 1 ? styles.iconBtnDisabled : styles.iconBtnDanger}
                                        title="Eliminar hoja"
                                        disabled={pages.length <= 1}
                                        onClick={() => onDelete(page.id)}
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {editable && (
                <div style={styles.editActions}>
                    <button type="button" style={styles.addBtn} onClick={onAdd}>+ Hoja</button>
                    {onDuplicate && (
                        <button type="button" style={styles.addBtn} onClick={onDuplicate}>Duplicar</button>
                    )}
                </div>
            )}
            <span style={styles.pager}>{index + 1} / {pages.length}</span>
            <button
                type="button"
                style={styles.arrow}
                disabled={index >= pages.length - 1}
                onClick={() => onSelect(pages[index + 1].id)}
                title="Hoja siguiente"
            >
                ›
            </button>
        </div>
    );
};

const styles = {
    bar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 8px',
        background: '#fff',
        border: '1px solid #e0d4f7',
        borderRadius: '10px',
        flexWrap: 'wrap'
    },
    tabs: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flex: 1,
        minWidth: 0,
        overflowX: 'auto'
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        border: '1px solid transparent',
        background: '#f8f9fa',
        borderRadius: '8px',
        padding: '2px 4px 2px 8px',
        flexShrink: 0
    },
    tabOn: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        border: '1px solid #5E35B1',
        background: '#f3e5f5',
        borderRadius: '8px',
        padding: '2px 4px 2px 8px',
        flexShrink: 0
    },
    tabBtn: {
        border: 'none',
        background: 'transparent',
        color: '#3c4043',
        fontWeight: 700,
        fontSize: '0.82rem',
        cursor: 'pointer',
        padding: '4px 6px',
        maxWidth: '180px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    tabActions: { display: 'flex', alignItems: 'center' },
    iconBtn: {
        border: 'none',
        background: 'transparent',
        color: '#5E35B1',
        cursor: 'pointer',
        fontSize: '0.85rem',
        lineHeight: 1,
        padding: '2px 4px'
    },
    iconBtnDanger: {
        border: 'none',
        background: 'transparent',
        color: '#c5221f',
        cursor: 'pointer',
        fontSize: '1.05rem',
        lineHeight: 1,
        padding: '2px 4px'
    },
    iconBtnDisabled: {
        border: 'none',
        background: 'transparent',
        color: '#bdbdbd',
        cursor: 'default',
        fontSize: '1.05rem',
        lineHeight: 1,
        padding: '2px 4px'
    },
    rename: {
        border: '1px solid #5E35B1',
        borderRadius: '6px',
        padding: '3px 6px',
        fontSize: '0.82rem',
        fontWeight: 700,
        width: '140px'
    },
    editActions: { display: 'flex', gap: '6px', flexShrink: 0 },
    addBtn: {
        border: '1px solid #d1c4e9',
        background: '#fff',
        color: '#4a148c',
        borderRadius: '7px',
        padding: '5px 10px',
        fontWeight: 700,
        fontSize: '0.78rem',
        cursor: 'pointer'
    },
    arrow: {
        border: '1px solid #dadce0',
        background: '#fff',
        color: '#5E35B1',
        borderRadius: '7px',
        width: '28px',
        height: '28px',
        fontSize: '1.2rem',
        lineHeight: 1,
        cursor: 'pointer',
        flexShrink: 0
    },
    pager: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#5f6368',
        minWidth: '42px',
        textAlign: 'center',
        flexShrink: 0
    },
    compact: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0
    },
    compactLabel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '92px',
        lineHeight: 1.15,
        cursor: 'pointer'
    },
    compactTitle: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#202124',
        maxWidth: '120px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    compactAdd: {
        border: '1px solid #dadce0',
        background: '#fff',
        color: '#5E35B1',
        borderRadius: '7px',
        width: '28px',
        height: '28px',
        fontSize: '1.1rem',
        lineHeight: 1,
        cursor: 'pointer',
        fontWeight: 700
    }
};

export default ReportPageNav;
